import * as dayjs from 'dayjs';

import {
  Account,
  AccountKind,
  CustomerAccount,
  CustomerAccountItem,
  GrowAccount,
  InyovaGrowAccountStatus,
  StepStatus,
  transactionsRangeDays,
  TransactionsRangeFilters,
  TransactionsTypeFilters,
  Customer,
  StaticDocument,
  Static3aDocsLocale,
  static3aDocs,
  STATIC_3A_SUPPORTED_LOCALES
} from '@inyova/models';

export const toFixed = (num: number, fixed: number | undefined) => {
  const pattern = `^-?\\d+(?:.\\d{0,${fixed || -1}})?`;
  const re = new RegExp(pattern);
  return num.toString().match(re)[0];
};

export function sortByStatus(a: CustomerAccountItem, b: CustomerAccountItem): number {
  const map = new Map<StepStatus | InyovaGrowAccountStatus, number>();
  map.set(StepStatus.INITIAL, 0);
  map.set(InyovaGrowAccountStatus.INITIAL, 0);
  map.set(StepStatus.ALMOST_PROSPECT, 1);
  map.set(StepStatus.PROSPECT, 2);
  map.set(StepStatus.ALMOST_CUSTOMER, 3);
  map.set(InyovaGrowAccountStatus.OPENED, 3);
  map.set(StepStatus.CUSTOMER, 4);
  map.set(InyovaGrowAccountStatus.FUNDED, 4);

  if (map.get(a.step_status) < map.get(b.step_status)) {
    return -1;
  }
  if (map.get(a.step_status) > map.get(b.step_status)) {
    return 1;
  }
  return 0;
}

export function sortByKind(a: CustomerAccountItem, b: CustomerAccountItem): number {
  const accountOrder = ['3b', 'grow', '3a', 'kid'];
  return accountOrder.indexOf(a.kind) - accountOrder.indexOf(b.kind);
}

export function isInvestAccount(account: Account): account is CustomerAccount {
  return account && account.kind !== 'grow';
}

export function isGrowAccount(account: Account): account is GrowAccount {
  return account && account.kind === 'grow';
}

export function is3aAccount(account: Account): account is CustomerAccount {
  return account && account.kind === '3a';
}

// step_status === 5 or Grow Funded/Opened
export function isCustomer(account: Account | CustomerAccountItem): boolean {
  if (!account) {
    return false;
  }
  const status = account.step_status ?? 0;
  const isGrow = account.kind === 'grow';

  return status >= 5 || (isGrow && status === InyovaGrowAccountStatus.OPENED);
}

// step_status < 5 or not grow Opened/funded
export function isNotYetCustomer(account: Account | CustomerAccountItem): boolean {
  return !isCustomer(account);
}

// step_status < 4 or not grow Opened/funded
export function isMaxAlmostCustomer(status: StepStatus | InyovaGrowAccountStatus): boolean {
  return status < 4;
}

// step_status < 3 or not grow Opened/funded (=== 1 or 2)
export function isMaxProspect(account: Account | CustomerAccountItem): boolean {
  if (!account) {
    return false;
  }
  const status = account.step_status ?? 0;
  const isGrow = account.kind === 'grow';

  return isGrow ? status < InyovaGrowAccountStatus.FUNDED : status < 3;
}

// step_status > 2
export function isMinProspect(account: Account | CustomerAccountItem): boolean {
  if (!account) {
    return false;
  }
  const status = account.step_status ?? 0;
  const isGrow = account.kind === 'grow';

  return isGrow ? status > InyovaGrowAccountStatus.INITIAL : status > 2;
}

// step_status > 3
export function isMinAlmostCustomer(status: StepStatus | InyovaGrowAccountStatus): boolean {
  return status >= 4;
}

export function getStorageItemsByPrefix(prefix: string): { key: string; value: string }[] {
  const archive = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      const value = localStorage.getItem(key);
      archive.push({ key, value });
    }
  }
  return archive;
}

export function storeItemsInLocalStorage(items: { key: string; value: string }[]): void {
  items.forEach((item) => {
    localStorage.setItem(item.key, item.value);
  });
}

export function getTransactionsRangeEndDate(rangeFilter: TransactionsRangeFilters): string {
  return dayjs().subtract(transactionsRangeDays[rangeFilter], 'day').format('YYYY-MM-DD');
}

const excludedTypeFiltersByKind: Partial<Record<AccountKind, Set<TransactionsTypeFilters>>> = {
  '3b': new Set([TransactionsTypeFilters.INTEREST, TransactionsTypeFilters.REPAYMENT, TransactionsTypeFilters.BUY, TransactionsTypeFilters.SELL]),
  kid: new Set([TransactionsTypeFilters.INTEREST, TransactionsTypeFilters.REPAYMENT, TransactionsTypeFilters.BUY, TransactionsTypeFilters.SELL]),
  '3a': new Set([
    TransactionsTypeFilters.INTEREST,
    TransactionsTypeFilters.MONEY_PROMOTIONS,
    TransactionsTypeFilters.REPAYMENT,
    TransactionsTypeFilters.BUY,
    TransactionsTypeFilters.SELL
  ])
};

export function getTypeFiltersForKind(filters: TransactionsTypeFilters[], kind: AccountKind): TransactionsTypeFilters[] {
  const excluded = excludedTypeFiltersByKind[kind];
  return excluded ? filters.filter((f) => !excluded.has(f)) : filters;
}

export function createExternal3aDocuments(customer: Customer, translate: (key: string) => string): StaticDocument[] {
  const locale = (STATIC_3A_SUPPORTED_LOCALES as readonly string[]).includes(customer.language) ? customer.language : 'de-CH';

  return static3aDocs.map((document) => ({
    id: `external-3a-${document.titleKey}`,
    type: 'static_document',
    attributes: {
      account_type: '3a',
      category: null,
      title: translate(`profile.documents.3a.document.${document.titleKey}`),
      date: customer.created_at,
      externalUrl: document.link[locale]
    }
  }));
}
